<script setup lang="ts">
/**
 * PAGE CAGNOTTE — message d'une personne de l'association : citation, photo, nom,
 * rôle et lien LinkedIn (Karim 2026-10-12).
 */
import { useRechercheHelpers } from "~/composables/useRechercheHelpers";
import type { TemoinCagnotte } from "./types";

const props = defineProps<{ temoin: TemoinCagnotte }>();
const { getImageUrl } = useRechercheHelpers();

const initiales = computed(() =>
  props.temoin.nom.split(/\s+/).filter(Boolean).slice(0, 2).map((m) => m.charAt(0).toUpperCase()).join(""),
);
</script>

<template>
  <figure class="flex flex-col justify-center rounded-[24px] bg-[#f4f6ff] px-7 py-9 text-center">
    <svg viewBox="0 0 32 32" class="mx-auto w-7 h-7 text-(--color-ink)" fill="currentColor" aria-hidden="true">
      <path d="M13 6c-5 1.6-8.5 5.8-8.5 11.6V26h9.2v-9H9.4c.2-3.6 2-6.2 5.2-7.6L13 6zm14.5 0c-5 1.6-8.5 5.8-8.5 11.6V26h9.2v-9h-4.3c.2-3.6 2-6.2 5.2-7.6L27.5 6z" />
    </svg>
    <blockquote class="mt-5 text-[21px] leading-[1.45] text-(--color-ink)" style="font-weight: 700 !important; letter-spacing: -0.01em">
      {{ temoin.message }}
    </blockquote>
    <figcaption class="mt-7 flex flex-col items-center">
      <img
        v-if="temoin.photo"
        :src="getImageUrl(temoin.photo, undefined, 160)"
        :alt="temoin.nom"
        class="w-16 h-16 rounded-full object-cover ring-4 ring-white shadow-sm"
        loading="lazy"
      />
      <span
        v-else
        class="w-16 h-16 rounded-full ring-4 ring-white shadow-sm bg-gradient-to-br from-[#dfe7ff] to-[#b9ccff] text-[#0c3097] text-lg flex items-center justify-center"
        style="font-weight: 700 !important"
      >{{ initiales }}</span>
      <span class="mt-3 inline-flex items-center gap-2 text-[15px] text-(--color-ink)" style="font-weight: 700 !important">
        {{ temoin.nom }}
        <a
          v-if="temoin.linkedin"
          :href="temoin.linkedin"
          target="_blank"
          rel="noopener"
          class="inline-flex items-center justify-center w-6 h-6 rounded-md bg-white text-[#0a66c2] ring-1 ring-gray-200 hover:ring-[#0a66c2] text-[11px] no-underline"
          style="font-weight: 800 !important"
          :aria-label="$t('campagne.pageCagnotte.profilLinkedin', { nom: temoin.nom })"
        >in</a>
      </span>
      <span v-if="temoin.role" class="mt-0.5 text-sm text-gray-500">{{ temoin.role }}</span>
    </figcaption>
  </figure>
</template>
